import * as React from "react";
import dayjs, { Dayjs } from "dayjs";
import { CalendarIcon, ChevronLeft, ChevronRight, X } from "lucide-react";

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

interface DateRangePickerProps {
  value?: [Dayjs, Dayjs] | null;
  onChange?: (range: [Dayjs, Dayjs] | null) => void;
  placeholder?: string;
  className?: string;
}

const WEEKDAYS = ["일", "월", "화", "수", "목", "금", "토"];

const buildMonthDays = (month: Dayjs) => {
  const startOfMonth = month.startOf("month");
  const startDay = startOfMonth.day();
  const startDate = startOfMonth.subtract(startDay, "day");
  return Array.from({ length: 42 }, (_, i) => startDate.add(i, "day"));
};

export function DateRangePicker({
  value,
  onChange,
  placeholder = "기간 선택",
  className,
}: DateRangePickerProps) {
  const [start, setStart] = React.useState<Dayjs | null>(
    value?.[0] ?? null
  );
  const [end, setEnd] = React.useState<Dayjs | null>(value?.[1] ?? null);
  const [viewMonth, setViewMonth] = React.useState<Dayjs>(
    (value?.[0] ?? dayjs()).startOf("month")
  );

  React.useEffect(() => {
    setStart(value?.[0] ?? null);
    setEnd(value?.[1] ?? null);
    if (value?.[0]) setViewMonth(value[0].startOf("month"));
  }, [value?.[0]?.valueOf(), value?.[1]?.valueOf()]);

  const label = React.useMemo(() => {
    if (start && end) {
      return `${start.format("YYYY-MM-DD")} ~ ${end.format("YYYY-MM-DD")}`;
    }
    if (start && !end) {
      return `${start.format("YYYY-MM-DD")} ~ 종료일`;
    }
    return placeholder;
  }, [start, end, placeholder]);

  const applyRange = (s: Dayjs, e: Dayjs) => {
    setStart(s);
    setEnd(e);
    setViewMonth(s.startOf("month"));
    onChange?.([s, e]);
  };

  const handleDayClick = (day: Dayjs) => {
    if (!start || (start && end)) {
      setStart(day);
      setEnd(null);
      setViewMonth(day.startOf("month"));
      return;
    }

    if (day.isBefore(start, "day")) {
      setStart(day);
      setEnd(null);
      setViewMonth(day.startOf("month"));
      return;
    }

    setEnd(day);
    onChange?.([start, day]);
  };

  const isInRange = (day: Dayjs) => {
    if (!start || !end) return false;
    return day.isAfter(start, "day") && day.isBefore(end, "day");
  };

  const renderMonth = (month: Dayjs) => {
    const days = buildMonthDays(month);
    return (
      <div className="space-y-2">
        <div className="text-center text-sm font-semibold">
          {month.format("YYYY년 M월")}
        </div>
        <div className="grid grid-cols-7 text-center text-xs text-muted-foreground">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-1">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {days.map((day) => {
            const outside = !day.isSame(month, "month");
            const isToday = day.isSame(dayjs(), "day");
            const isStart = start && day.isSame(start, "day");
            const isEnd = end && day.isSame(end, "day");
            const inRange = isInRange(day);

            return (
              <button
                type="button"
                key={day.format("YYYY-MM-DD")}
                onClick={() => handleDayClick(day)}
                className={cn(
                  "h-9 w-9 rounded-md text-sm transition-colors",
                  outside && "text-muted-foreground/60",
                  inRange && "bg-primary/10",
                  (isStart || isEnd) && "bg-primary text-primary-foreground",
                  isToday && !isStart && !isEnd && "ring-1 ring-primary/40"
                )}
              >
                {day.date()}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn(
            "h-10 min-w-[240px] justify-start text-left font-normal",
            !start && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[700px] max-w-[90vw]" align="start">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="text-sm font-semibold">기간 선택</div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMonth((m) => m.subtract(1, "month"))}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setViewMonth((m) => m.add(1, "month"))}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {(start || end) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-1"
                  onClick={() => {
                    setStart(null);
                    setEnd(null);
                    onChange?.(null);
                  }}
                >
                  <X className="h-3 w-3" />
                  초기화
                </Button>
              )}
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            {renderMonth(viewMonth)}
            {renderMonth(viewMonth.add(1, "month"))}
          </div>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() =>
                applyRange(dayjs().startOf("month"), dayjs().endOf("day"))
              }
            >
              이번 달
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => applyRange(dayjs().subtract(6, "day"), dayjs())}
            >
              최근 7일
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => applyRange(dayjs().subtract(29, "day"), dayjs())}
            >
              최근 30일
            </Button>
          </div>

          <div className="text-xs text-muted-foreground">
            시작일을 선택한 뒤 종료일을 선택하세요.
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
